// AUTO-GENERATED by scripts/generate-contracts.mjs from the .md sidecar.
// Hand edits will be overwritten on the next run. To customise this
// component's contract (add actions, structured props, custom control
// kinds, glyph pickers), remove this file's auto-generated marker and
// hand-author it — the generator skips files without the marker.

import { z } from "zod";
import { contract } from "@gradeui/contracts";

export const SidebarContract = contract({
  name: "Sidebar",
  description: "Vertical app navigation. Drop inside `<AppShellNav placement=\"side\">` for full-page layouts. Compound API — `<SidebarHeader>` for brand, `<SidebarContent>` for the scrollable body of `<SidebarSection>` + `<SidebarItem>` rows, `<SidebarFooter>` for user / settings chrome. For top nav reach for TopMenu; for command-palette style search reach for Command.",
  import: "@gradeui/ui",
  aliases: ["sidebar","side menu","sidemenu","navigation sidebar","app sidebar","side nav","side nav rail","master pane","sidebarmenu","navigation rail","react native drawer"],
  subcomponents: ["SidebarHeader","SidebarContent","SidebarFooter","SidebarSection","SidebarItem"],
  composesWith: ["AppShell (inside AppShellNav)","Avatar (in Footer)","Tooltip (auto-wrapped on collapsed items)","Button (asChild for custom routing)"],
  styleDefaults: {"Sidebar":"relative flex h-full flex-col bg-card text-card-foreground","SidebarHeader":"flex shrink-0 items-center border-b border-border","SidebarContent":"flex-1 overflow-y-auto overflow-x-hidden","SidebarFooter":"shrink-0 border-t border-border","SidebarSection":"px-[var(--gds-sidebar-section-px,0.5rem)] space-y-[var(--gds-sidebar-section-gap,0.125rem)]","SidebarItem":"truncate font-normal text-muted-foreground","SidebarTreeItem":"group flex w-full items-center gap-1.5 rounded-md py-1 text-sm font-medium transition-colors"},
  props: {
  "collapsed": {
      schema: z.boolean().optional(),
      design: "knob",
      description: "controlled collapsed state (wire onCollapsedChange when set)",
  },
  "defaultCollapsed": {
      schema: z.boolean().optional(),
      design: "knob",
      description: "uncontrolled initial value (default false)",
  },
  "onCollapsedChange": {
      schema: z.unknown().optional(),
      design: "event",
  },
  "collapsible": {
      schema: z.boolean().optional(),
      design: "knob",
      description: "show the affordance for the user to collapse (default true)",
  },
  "variant": {
      schema: z.enum(["rail", "panel"]).optional(),
      design: "knob",
      description: "outer chrome treatment. `rail` (default) is the classic nav rail with a single right-border + tracked width via `--gds-sidebar-width`; drops cleanly into `<AppShellNav placement=\"side\">`. `panel` is a card-style floating sidebar with full border + rounded corners + parent-controlled width; use when the sidebar is one of several adjacent panes in a body row (e.g. Projects | Canvas | Settings). The compound children (Header/Content/Footer/Section/Item) are identical in both treatments.",
  },
  "bordered": {
      schema: z.boolean().optional(),
      design: "knob",
      description: "draw the OUTER edge: the right-hand rule in `rail`, the full outline in `panel` (default true). Set false where the rail's own `bg-card` already separates it from the content beside it and the rule reads as an artefact, which is typical on dark high-contrast themes. A borderless rail keeps its width transition and a borderless panel keeps its rounding and clipping. Controls the outer edge ONLY: the rules under SidebarHeader and above SidebarFooter are internal structure and stay.",
  },
  "title": {
      schema: z.unknown().optional(),
      design: "plumbing",
      description: "group label, tracking-wide muted styling; hidden when sidebar is collapsed. CASE: static (non-collapsible) headers historically render UPPERCASE (Notion / Linear / Slack-style \"GAMES\", \"FAVORITES\"); collapsible headers render the authored case. Control it explicitly with titleTransform.",
  },
  "titleTransform": {
      schema: z.enum(["uppercase", "none"]).optional(),
      design: "knob",
      description: "title casing for BOTH header variants. \"none\" renders the authored case (sentence-case headers like a \"Recents\" list); \"uppercase\" forces the shouty group label. Unset = the per-variant legacy default above.",
  },
  "icon": {
      schema: z.unknown().optional(),
      design: "plumbing",
      description: "optional icon beside the title",
  },
  "trailing": {
      schema: z.unknown().optional(),
      design: "plumbing",
      description: "**action(s) on the right edge of the header** — the canonical \"+\" / \"...\" slot (Notion's \"+ Add page\" next to Pages, Linear's \"+\" next to Favorites, Slack's \"+\" next to Channels). Pointer events isolated so a Button here doesn't toggle collapse.",
  },
  "defaultExpanded": {
      schema: z.boolean().optional(),
      design: "knob",
      description: "initial open state (default true)",
  },
  "badge": {
      schema: z.unknown().optional(),
      design: "plumbing",
      description: "trailing count / label (hidden when collapsed)",
  },
  "active": {
      schema: z.boolean().optional(),
      design: "knob",
      description: "current route; adds aria-current=\"page\"",
  },
  "href": {
      schema: z.string().optional(),
      design: "content",
      description: "renders as <a>; for routing use `asChild` with your link component",
  },
  "asChild": {
      schema: z.boolean().optional(),
      design: "plumbing",
      description: "wrap a custom link (<Link href> from Next.js etc.) via Radix Slot",
  },
  "asButton": {
      schema: z.boolean().optional(),
      design: "knob",
      description: "render as <button> for action rows (open dialog, log out)",
  },
  "disabled": {
      schema: z.boolean().optional(),
      design: "knob",
  },
  "collapsedLabel": {
      schema: z.unknown().optional(),
      design: "plumbing",
      description: "tooltip override when sidebar is collapsed (defaults to children text)",
  },
  "size": {
      schema: z.enum(["sm", "md"]).optional(),
      design: "knob",
      description: "row size. `md` (default) is the standard `text-sm font-medium` nav row; `sm` is `text-xs` + lighter weight + tighter padding for visually subordinate rows (nested screens under a project, sub-pages under a section). Active state still wins on color + weight so the current row pops at either size.",
  },
  "description": {
      schema: z.unknown().optional(),
      design: "plumbing",
      description: "secondary line beneath the label (metadata like 'Edited 2m ago', '12 items', a brief description). Row layout adapts: label + description stacked vertically; icon vertically-centered against the stack; badge stays on trailing edge. Hidden when sidebar collapsed.",
  },
  },
});
